import React, { useEffect, useState } from 'react';
import './OrganizationManager.css';
import { BASE_URL } from './Config';
import { FiMenu, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import logoImage from '../resources/1000028180.jpeg';

export default function OrganizationManager() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newOrg, setNewOrg] = useState('');
  const [editOrgId, setEditOrgId] = useState(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/organization/get_organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newOrg.trim()) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/organization/create_organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newOrg.trim() }),
      });
      if (!response.ok) throw new Error('Failed to create organization');
      setNewOrg('');
      setShowCreateModal(false);
      fetchOrganizations();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (org) => {
    // setEditOrgId(org.org_id);
    setEditOrgId(org.org_id);
    setEditOrgName(org.name);
  };

  const handleEditSave = async (org) => {
    if (!editOrgName.trim()) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/organization/${org.org_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editOrgName.trim() }),
      });
      if (!response.ok) throw new Error('Failed to update organization');
      setEditOrgId(null);
      setEditOrgName('');
      fetchOrganizations();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = (org) => {
    setOrgToDelete(org);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!orgToDelete) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/organization/${orgToDelete.org_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete organization');
      setShowDeleteModal(false);
      setOrgToDelete(null);
      fetchOrganizations();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const logout = () => {
    navigate("/logout");
  };

  const menuBtnStyle = {
    background: 'none',
    border: 'solid 1px #f1f1f1',
    textAlign: 'left',
    padding: '12px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.18s',
    borderBottom: '1px solid #f1f1f1',
    color: 'gray',
  };

  return (
    <div className="org-manager-bg">
      {/* Header */}
      <div className="org-header">
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="header-logo-container">
                                  <img
                        src={logoImage}
                        alt="Power Mind Metrics Logo"
                        style={{ height: "90px", width: "100px", marginRight: "10px" }}
                      />
            <h1 className="header-logo">Power Mind Metrics</h1>
          </div>
          <nav className="header-nav" style={{ marginLeft: 'auto' }}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="menu-button"
              aria-label="Open menu"
            >
              <FiMenu size={24} />
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div className="menu-dropdown">
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/AdminDashboard'); }}>Admin Dashboard</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/admin/sub-admins'); }}>Manage Subadmin</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/manage-users'); }}>Manage Users</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/organizations'); }}>Manage Organization</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/graph-report'); }}>Graph Report</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); logout(); }}>Logout</button>
          </div>
        )}
      </div>

      {/* Main Organization Card */}
      <div className="org-manager-container">
        <div className="org-manager-header-row">
          <h2 className="org-manager-title">Manage Organizations</h2>
          <button
            className="create-org-btn"
            onClick={() => setShowCreateModal(true)}
          >
            Create Organization
          </button>
        </div>
        {/* Table remains below */}
        {error && <div className="org-error">{error}</div>}
        {loading ? (
          <div className="org-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <table className="org-table">
            <thead>
              <tr>
                <th>Index</th>
                <th>Organization Name</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org, i) => (
                <tr key={org.org_id}>
                  <td>{i + 1}</td>
                  <td>
                    {editOrgId === org.org_id ? (
                      <input
                        type="text"
                        value={editOrgName}
                        onChange={e => setEditOrgName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditSave(org);
                          if (e.key === 'Escape') setEditOrgId(null);
                        }}
                        autoFocus
                        className="org-edit-input"
                        disabled={actionLoading}
                      />
                    ) : (
                      org.name
                    )}
                  </td>
                  <td>
                    {editOrgId === org.org_id ? (
                      <>
                        <button
                          className="org-save-btn"
                          onClick={() => handleEditSave(org)}
                          disabled={actionLoading}
                        >
                          Save
                        </button>
                        <button
                          className="org-cancel-btn"
                          onClick={() => setEditOrgId(null)}
                          disabled={actionLoading}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="org-icon-btn"
                          onClick={() => handleEdit(org)}
                          disabled={actionLoading}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <FiEdit2 size={20} />
                        </button>
                        <button
                          className="org-icon-btn org-delete-icon-btn"
                          onClick={() => handleDelete(org)}
                          disabled={actionLoading}
                          aria-label="Delete"
                          title="Delete"
                        >
                          <FiTrash2 size={20} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {organizations.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>
                    No organizations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {/* Modal for create organization */}
        {showCreateModal && (
          <div className="org-modal-overlay">
            <div className="org-modal">
              <h3 className="org-modal-title">Create Organization</h3>
              <form onSubmit={handleCreate}>
                <label className="org-modal-label">Name of the Organization</label>
                <input
                  className="org-modal-input"
                  type="text"
                  placeholder="Enter organization name"
                  value={newOrg}
                  onChange={e => setNewOrg(e.target.value)}
                  disabled={actionLoading}
                  autoFocus
                />
                <div className="org-modal-actions">
                  <button
                    type="button"
                    className="org-modal-cancel"
                    onClick={() => { setShowCreateModal(false); setNewOrg(''); }}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="org-modal-submit"
                    disabled={actionLoading || !newOrg.trim()}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showDeleteModal && (
          <div className="org-modal-overlay">
            <div className="org-modal">
              <h3 className="org-modal-title">Delete Organization</h3>
              <div style={{ marginBottom: '2rem', textAlign: 'center', color: '#444', fontSize: '1.08rem' }}>
                Are you sure you want to delete <b>{orgToDelete?.name}</b>?
              </div>
              <div className="org-modal-actions">
                <button
                  type="button"
                  className="org-modal-cancel"
                  onClick={() => { setShowDeleteModal(false); setOrgToDelete(null); }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="org-modal-submit"
                  onClick={confirmDelete}
                  disabled={actionLoading}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '12px 0',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif',
        zIndex: 100
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: 'rgba(75, 75, 75, 0.8)',
          fontWeight: 400
        }}>
          © 2025 All rights reserved Power Mind Metrics
        </p>
      </div>
    </div>
  );
}
